import DeleteIcon from '@mui/icons-material/DeleteOutline';
import { Button, IconButton, InputAdornment, Stack } from '@mui/material';
import { FlowFhirEndpoint, FlowLookbackDurationUnit } from 'flow-schema';
import { type ComponentType } from 'react';

import { Text } from 'common';

import { useReactFlowViewContext } from '../../../context/context';
import { BlockFreeSoloMultiAutocomplete } from '../../ConfigFields/Autocomplete';
import { BlockValueTemplateField } from '../../ConfigFields/CodeMirror/FlowTemplate';
import { BlockDurationField } from '../../ConfigFields/Duration';
import {
  ParentBlockSelectField,
  getSourceBlockNameHelperText,
} from '../../ConfigFields/SelectField';
import { BlockTextField } from '../../ConfigFields/TextField';
import { useSelectedNodeField } from '../../ConfigFields/useSelectedNodeField';

interface FhirVariant {
  Config: ComponentType | undefined;
}

const NO_CONFIG_VARIANT: FhirVariant = {
  Config: undefined,
};

const EHR_ID_HELPER_TEXT = 'Enter the EHRID for the patient.';

const FIRST_NAME_HELPER_TEXT = 'Enter the First Name for the patient.';

const LAST_NAME_HELPER_TEXT = 'Enter the Last Name for the patient.';

const DATE_OF_BIRTH_HELPER_TEXT = 'Enter the Date of Birth for the patient.';

const NPI_HELPER_TEXT = 'Enter the NPI for the practitioner.';

const APPOINTMENT_FILTERS_AFTER_HELPER_TEXT = `Determine the date after which we search for appointments, calculated as flow run date plus duration`;

export const FHIR_VARIANTS: Record<FlowFhirEndpoint, FhirVariant> = {
  [FlowFhirEndpoint.GetPatient]: {
    Config: () => {
      return (
        <Stack spacing={2}>
          <Text variant="heading5" bold>
            Patient Search Filters
          </Text>
          <BlockValueTemplateField
            fieldName="config.patientSearchFilters.ehrId"
            label="EHR ID"
            helperText={EHR_ID_HELPER_TEXT}
          />
          <BlockValueTemplateField
            fieldName="config.patientSearchFilters.firstName"
            label="First Name"
            required
            helperText={FIRST_NAME_HELPER_TEXT}
          />
          <BlockValueTemplateField
            fieldName="config.patientSearchFilters.lastName"
            label="Last Name"
            required
            helperText={LAST_NAME_HELPER_TEXT}
          />
          <BlockValueTemplateField
            fieldName="config.patientSearchFilters.dateOfBirth"
            label="Date of Birth"
            required
            helperText={DATE_OF_BIRTH_HELPER_TEXT}
          />
          <BlockValueTemplateField
            fieldName="config.patientSearchFilters.phoneNumber"
            label="Phone Number"
            required
            helperText="Enter the Phone Number for the patient."
          />
          <BlockValueTemplateField
            fieldName="config.patientSearchFilters.patientId"
            label="Patient ID"
            helperText="Enter the Patient ID for the patient."
          />
          <BlockValueTemplateField
            fieldName="config.patientSearchFilters.email"
            label="Email"
            helperText="Enter the Email for the patient."
          />
          <BlockValueTemplateField
            fieldName="config.patientSearchFilters.zipCode"
            label="Zip Code"
            helperText="Enter the Zip Code for the patient."
          />
          <BlockValueTemplateField
            fieldName="config.patientSearchFilters.patientEhrIdToMatch"
            label="EHR_ID for Matching"
            helperText="If specified, this EHR ID will be used to look up the patient directly."
          />
        </Stack>
      );
    },
  },
  [FlowFhirEndpoint.PractitionerSearch]: {
    Config: () => {
      return (
        <Stack spacing={2}>
          <Text variant="heading5" bold>
            Practitioner Filter
          </Text>
          <BlockValueTemplateField
            fieldName="config.practitionerFilters.firstName"
            label="First Name"
            helperText="Enter the First Name for the practitioner."
          />
          <BlockValueTemplateField
            fieldName="config.practitionerFilters.lastName"
            label="Last Name"
            helperText="Enter the Last Name for the practitioner."
          />
          <BlockValueTemplateField
            fieldName="config.practitionerFilters.npi"
            label="NPI"
            helperText={NPI_HELPER_TEXT}
          />
          <ParentBlockSelectField
            fieldName="config.practitionerFilters.provider.sourceBlockName"
            label="Provider Source"
            helperText={getSourceBlockNameHelperText('provider')}
          />
        </Stack>
      );
    },
  },
  [FlowFhirEndpoint.ListEncounters]: NO_CONFIG_VARIANT,
  [FlowFhirEndpoint.BookedAppointments]: {
    Config: () => {
      return (
        <Stack spacing={2}>
          <Text variant="heading5" bold>
            Booked Appointment Filter
          </Text>
          <BlockDurationField
            fieldName="config.appointmentFilters.after"
            unitFieldNameSuffix="unit"
            allowedUnits={Object.values(FlowLookbackDurationUnit)}
            valueHelperText={APPOINTMENT_FILTERS_AFTER_HELPER_TEXT}
            valueLabel="After"
          />
        </Stack>
      );
    },
  },
  [FlowFhirEndpoint.LabValues]: {
    Config: () => {
      return (
        <Stack spacing={2}>
          <Text variant="heading5" bold>
            Lab Value Filter
          </Text>
          <BlockFreeSoloMultiAutocomplete
            fieldName="config.labValueFilters.categories"
            label="Categories"
          />
        </Stack>
      );
    },
  },
  [FlowFhirEndpoint.ServiceRequests]: {
    Config: () => {
      return (
        <Stack spacing={2}>
          <Text variant="heading5" bold>
            Service Request Filter
          </Text>
          <BlockValueTemplateField
            fieldName="config.serviceRequestFilters.providerNpi"
            label="Provider NPI"
            helperText="Provide unique identifier associated with the provider nationally."
          />
          <BlockValueTemplateField
            fieldName="config.serviceRequestFilters.providerId"
            label="Provider ID"
            helperText="Provider EHR specific identifier for the provider."
          />
          <BlockValueTemplateField
            fieldName="config.serviceRequestFilters.encounterId"
            label="Encounter ID"
            helperText="Provide EHR specific identifier for the encounter."
          />
        </Stack>
      );
    },
  },
  [FlowFhirEndpoint.Medications]: NO_CONFIG_VARIANT,
  [FlowFhirEndpoint.Conditions]: {
    Config: () => {
      return (
        <Stack spacing={2}>
          <Text variant="heading5" bold>
            Condition Filter
          </Text>
          <BlockFreeSoloMultiAutocomplete
            fieldName="config.conditionFilters.verificationStatuses"
            label="Verification Statuses"
            helperText="Provider the verification status to support the clinical status of the condition."
          />
        </Stack>
      );
    },
  },
  [FlowFhirEndpoint.Documents]: NO_CONFIG_VARIANT,
  [FlowFhirEndpoint.Binaries]: {
    Config: () => {
      const { value: binaryUrls, setValue: setBinaryUrls } = useSelectedNodeField<string[]>(
        'config.binaryFilters.binaryUrls'
      );
      const { isEditable } = useReactFlowViewContext();
      return (
        <Stack spacing={2}>
          <Text variant="heading5" bold>
            Binary Filter
          </Text>
          <BlockTextField
            fieldName="config.binaryFilters.bucketName"
            label="Bucket Name"
            required
          />
          <BlockTextField
            fieldName="config.binaryFilters.pathPrefix"
            label="Path Prefix"
            required
          />
          <BlockTextField
            fieldName="config.binaryFilters.binaryUrls.0"
            label="Binary URL 1"
            required
          />
          {binaryUrls?.map((_url, index) => {
            if (index === 0) {
              // Skip the first URL from above.
              return null;
            }
            return (
              <BlockTextField
                key={index}
                fieldName={`config.binaryFilters.binaryUrls.${index}`}
                label={`Binary URL ${index + 1}`}
                required
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setBinaryUrls(binaryUrls.filter((_, i) => i !== index))}
                          aria-label={`Delete Binary URL ${index + 1}`}
                          disabled={!isEditable}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />
            );
          })}
          <Button variant="outlined" onClick={() => setBinaryUrls([...(binaryUrls ?? []), ''])}>
            Add Binary URL
          </Button>
        </Stack>
      );
    },
  },
  [FlowFhirEndpoint.SurgicalHistories]: NO_CONFIG_VARIANT,
  [FlowFhirEndpoint.Coverages]: NO_CONFIG_VARIANT,
};
